import { autoinject, bindable } from 'aurelia-framework';
import { PubSub } from 'lib/event/PubSub';
import { AsyncErrorHandler, DialogHandler } from 'lib/ui';
import { ToastService } from 'services';
import { MooringProductionOrderService } from 'services/mooring';
import { CreateEditHourItemDialog } from './create-edit-hour-item-dialog';
import { HourProductionOrderDto, MooringProductionTaskType } from 'models/mooring';
import { sortDateDesc } from 'lib/helpers/sorting';
import { MooringProductionOrderEvents } from './events';

@autoinject
export class HoursContainer {
  @bindable
  productionOrderId: number;

  @bindable
  taskOptions?: MooringProductionTaskType[];

  @bindable
  showBanner: boolean = true;

  protected hours: HourProductionOrderDto[] = [];

  protected totalHours: number = 0;
  protected totalWorkers: number = 0;

  constructor(
    private productionOrderService: MooringProductionOrderService,
    private toast: ToastService,
    private pubsub: PubSub,
    private dialog: DialogHandler
  ) {}

  protected bind() {
    this.pubsub.sub('entity:updated', (data) => {
      if (data?.name === MooringProductionOrderEvents.HOURS_ENTITY) {
        void this.getHours();
      }
    });

    this.pubsub.sub('entity:deleted', (data) => {
      if (data?.name === MooringProductionOrderEvents.HOURS_ENTITY) {
        void this.getHours();
      }
    });

    void this.getHours();
  }

  protected attached() {
    if (!this.productionOrderId || this.productionOrderId <= 0) {
      throw new Error('productionOrderId is required');
    }
  }

  @AsyncErrorHandler
  protected onSave({ canNavigateOnSave }: { canNavigateOnSave: boolean }) {
    if (!this.productionOrderId || this.productionOrderId <= 0) {
      throw new Error('productionOrderId is required');
    }

    this.toast.showUpdated();
    this.pubsub.publish('tabs:change-guard-ok', null);
  }

  @AsyncErrorHandler
  protected async getHours() {
    const { data } = await this.productionOrderService.getHours(this.productionOrderId);

    const selectedTasks = new Set<MooringProductionTaskType>(this.taskOptions);
    this.hours = data
      .filter((hour) => {
        if (!selectedTasks.size) return true;
        return selectedTasks.has(hour.Task);
      })

      .sort(sortDateDesc('Date'));

    this.totalHours = this.hours.reduce((acc, hour) => acc + hour.Hours, 0);
    this.totalWorkers = new Set<string>(this.hours.flatMap((h) => h.UserId))?.size;
  }

  protected async onAddHour() {
    const { wasCancelled } = await this.dialog.open(CreateEditHourItemDialog, {
      productionOrderId: this.productionOrderId,
      taskOptions: this.taskOptions,
    });

    if (!wasCancelled) {
      void this.getHours();
    }
  }

  unbind() {
    this.pubsub.unsub();
  }

  detached() {
    this.pubsub.unsub();
  }
}
